// src/components/hub/work/CreateTaskModal.js
import React, { useRef, useState, useEffect } from "react";
// AFTER
import {
  popupBg,
  popupBorder,
  popupText,
  popupSubtext,
  popupBtn,
  popupInput,
  PRIORITY_OPTIONS,
  PRIORITY_LABELS,
} from "../constants";
import { filterPillClass } from "../../../shared/format";
import { uploadTaskFile, attachWork } from "../../../../api/hub/work";

const initialMeta = {
  work_type: "internal",
  owner_id: null,
  client_id: null,
  service: "",
  priority: null,
  due: null,
  tags: [],
};

// "How often" cadence options for the Repeat-this-task section (work-page-fixes
// §B). The label is what the user sees; the value is the cadence string the
// backend stores (fn_recurring_from_task.p_cadence). Hoisted to module scope so
// the arrays are NOT reallocated on every render — matches the convention in
// RepeatTaskModal.js and RepeatingView.js.
const CADENCE_OPTIONS = [
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Every month" },
  { value: "quarterly", label: "Every 3 months" },
  { value: "yearly", label: "Every year" },
];

const WEEKDAY_OPTIONS = [
  { value: 0, label: "Sunday" }, { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" }, { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" }, { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// Cadences that need a day-of-month picker (1–31). Weekly uses a weekday
// picker instead; daily/biweekly/yearly have no "On" field at all.
const DAY_OF_MONTH_CADENCES = new Set(["monthly", "quarterly"]);

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="opacity-60 shrink-0"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

// Generic file icon for non-image files (mirrors lucide-react's File glyph).
// Image types show their object-URL thumbnail instead.
const FileIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

// Paperclip icon (same glyph as AttachmentList) for the drop zone trigger.
const PaperclipIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

// Spinner shown on each file row while its upload is in flight.
const SpinnerIcon = ({ className = "" }) => (
  <svg
    className={`animate-spin ${className}`}
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const CreateTaskModal = ({
  open,
  onClose,
  onCreate,
  workTypeOptions = [],
  allFilterOptions = { owners: [], services: [], clients: [] },
  existingTags = [],
  initialValues,
}) => {
  const [meta, setMeta] = useState(initialMeta);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownQuery, setDropdownQuery] = useState("");
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ─── Files (work-page-fixes §C) ────────────────────────────────────────
  // Pending files chosen before Create. Each entry: { key, file, status,
  // error, thumbUrl }. status is "pending" | "uploading" | "done" | "failed".
  // thumbUrl is an object URL generated for image/* files (revoked on remove
  // and on unmount) so the list can preview without uploading first.
  const [files, setFiles] = useState([]);
  // The id of the task created by onCreate — needed to run uploads after the
  // task exists and for the per-file Retry buttons. null until onCreate returns.
  const [createdTaskId, setCreatedTaskId] = useState(null);
  // Repeat-this-task section state (work-page-fixes §B). When repeatEnabled is
  // false the section is collapsed and the submit handler passes null repeat
  // settings to the parent (no repeating rule created). dayOfWeek is only
  // meaningful for the weekly cadence; dayOfMonth for monthly/quarterly.
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatCadence, setRepeatCadence] = useState("weekly");
  const [repeatDayOfWeek, setRepeatDayOfWeek] = useState(1);
  const [repeatDayOfMonth, setRepeatDayOfMonth] = useState(1);
  // Hidden file input ref (Choose files button) and a drag-over highlight flag.
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  // Mirror of `files` for the unmount/reset cleanup so it can revoke object
  // URLs without reading stale state. Updated every render (cheap).
  const filesRef = useRef([]);
  filesRef.current = files;

  // Append new FileList entries (picker, drop, or paste) as pending. Only
  // image/* files get an object URL for the thumbnail preview.
  const addFiles = (newFiles) => {
    const arr = Array.from(newFiles || []);
    if (!arr.length) return;
    const entries = arr.map((file) => ({
      key:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`,
      file,
      status: "pending",
      error: null,
      thumbUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setFiles((prev) => [...prev, ...entries]);
  };

  // Remove one entry by key and revoke its thumbnail URL (if any).
  const removeFile = (key) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.key === key);
      if (target?.thumbUrl) URL.revokeObjectURL(target.thumbUrl);
      return prev.filter((f) => f.key !== key);
    });
  };

  // Revoke every thumbnail URL when the modal unmounts so object URLs don't
  // leak across open/close cycles.
  useEffect(() => {
    return () => {
      filesRef.current.forEach((f) => {
        if (f.thumbUrl) URL.revokeObjectURL(f.thumbUrl);
      });
    };
  }, []);

  // Seed the form from `initialValues` each time it opens (a promise turned into a task
  // arrives with its title, client and description). Keyed on `open` only: the caller's
  // object is rebuilt every render and must not overwrite what the user is typing.
  useEffect(() => {
    if (!open || !initialValues) return;
    setTitle(initialValues.title || "");
    setMeta((prev) => ({ ...prev, client_id: initialValues.client_id ?? prev.client_id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const reset = () => {
    setMeta(initialMeta);
    setOpenDropdown(null);
    setTitle("");
    setTitleError(false);
    if (descRef.current) descRef.current.value = "";
    // Revoke any pending thumbnail URLs and clear the files list + created
    // task id so the next open starts clean.
    filesRef.current.forEach((f) => {
      if (f.thumbUrl) URL.revokeObjectURL(f.thumbUrl);
    });
    setFiles([]);
    setCreatedTaskId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    // Reset the repeat-this-task section so the next open starts collapsed
    // with the default cadence (work-page-fixes §B).
    setRepeatEnabled(false);
    setRepeatCadence("weekly");
    setRepeatDayOfWeek(1);
    setRepeatDayOfMonth(1);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Upload one file entry to the work-attachments bucket, then call
  // fn_work_attach with the storage path + metadata. Updates the entry's
  // status in `files` (uploading → done/failed). Returns true on success,
  // false on failure (never throws — the caller drives the batch with
  // Promise.all). The safe storage path uses the task id, a timestamp, and the
  // filename with non-alphanumeric characters replaced by underscores
  // (work-page-fixes §C). If the upload fails, fn_work_attach is NOT called
  // for that file — the task creation still succeeds.
  const uploadOne = async (taskId, entry) => {
    const safeName = entry.file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${taskId}/${Date.now()}-${safeName}`;
    setFiles((prev) =>
      prev.map((f) => (f.key === entry.key ? { ...f, status: "uploading", error: null } : f))
    );
    try {
      await uploadTaskFile(path, entry.file);
      await attachWork(taskId, {
        filename: entry.file.name,
        storage_path: path,
        content_type: entry.file.type,
        size_bytes: entry.file.size,
        title: entry.file.name,
      });
      setFiles((prev) =>
        prev.map((f) => (f.key === entry.key ? { ...f, status: "done" } : f))
      );
      return true;
    } catch (e) {
      setFiles((prev) =>
        prev.map((f) =>
          f.key === entry.key
            ? { ...f, status: "failed", error: e?.message || "Upload failed" }
            : f
        )
      );
      return false;
    }
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setTitleError(true);
      return;
    }
    const payload = {
      title: trimmedTitle,
      description: (descRef.current?.value || "").trim() || null,
      work_type: meta.work_type || "internal",
      owner_id: meta.owner_id || null,
      client_id: meta.client_id || null,
      service: meta.service || null,
      priority: meta.priority || null,
      due: meta.due || null,
      status: null,
      tags: meta.tags.length > 0 ? meta.tags : null,
    };
    // Build the repeat settings handed to the parent (work-page-fixes §B).
    // null when the toggle is off or no cadence is selected — the parent then
    // skips the fn_recurring_from_task call. Only forward the day picker that
    // matches the chosen cadence (weekday for weekly, day-of-month for
    // monthly/quarterly); the others are sent as null.
    const repeatSettings =
      repeatEnabled && repeatCadence
        ? {
            cadence: repeatCadence,
            dayOfWeek: repeatCadence === "weekly" ? repeatDayOfWeek : null,
            dayOfMonth: DAY_OF_MONTH_CADENCES.has(repeatCadence)
              ? repeatDayOfMonth
              : null,
          }
        : null;
    setSubmitting(true);
    try {
      // onCreate creates the task (fn_work_create) and opens the slide-out in
      // the parent. It returns the new task id so we can attach files to it.
      // repeatSettings (work-page-fixes §B) is null when the Repeat section is
      // collapsed — the parent then skips the fn_recurring_from_task call.
      const newTaskId = await onCreate(payload, repeatSettings);
      // No id returned (parent handled the error) or no files to upload —
      // close the modal. The slide-out is already open when there's an id.
      if (!newTaskId || !files.length) {
        reset();
        onClose();
        return;
      }
      // Store the id so the per-file Retry buttons can re-upload a single file.
      setCreatedTaskId(newTaskId);
      // Upload every pending file. A failed upload marks the entry "failed"
      // but does NOT abort the others or the task creation (work-page-fixes §C).
      const pending = files.filter((f) => f.status === "pending");
      const results = await Promise.all(
        pending.map((f) => uploadOne(newTaskId, f))
      );
      // If every file succeeded, close the modal (the slide-out is already
      // open behind it). If any failed, keep the modal open so the user can
      // use Retry — the task and any successful attachments are already saved.
      if (results.every(Boolean)) {
        reset();
        onClose();
      }
    } catch {
      /* Parent handles error display */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
      onClick={handleClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") handleClose();
      }}
      onPaste={(e) => {
        const pasted = e.clipboardData?.files;
        if (pasted && pasted.length) addFiles(pasted);
      }}
      tabIndex={-1}
    >
      <div
        className={`${popupBg} ${popupBorder} ${popupText} rounded-lg p-4 md:p-6 w-[800px] max-w-[94vw] max-h-[90vh] flex flex-col shadow-xl border-t-2 border-blue-500`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={`text-sm font-medium ${popupText} mb-4`}>New Task</h3>

        {/* Scrollable body — keeps the form within the viewport on small
            screens. The footer (Cancel/Create) is a flex sibling below this
            region, so the buttons stay pinned while only this area scrolls.
            (work-page-fixes Item 1) */}
        <div className="overflow-y-auto flex-1 min-h-0">

        {/* Title */}
        <label className="block text-xs text-neutral-500 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          autoFocus
          ref={titleRef}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setTitleError(false);
          }}
          placeholder="Task Title"
          onKeyDown={(e) => {
            if (e.key === "Enter" && title.trim()) handleSubmit();
            if (e.key === "Escape") handleClose();
          }}
          className={`w-full text-lg font-medium bg-transparent border-b outline-none py-2 mb-1 text-gray-900 dark:text-white ${
            titleError ? "border-red-500" : "border-neutral-700 focus:border-blue-500"
          }`}
        />
        {titleError && (
          <p className="text-xs text-red-500 mb-3">Title is required</p>
        )}

              {/* Dropdown row 1 — Owner + Client */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Owner */}
          <div>
            <label className="block text-xs text-neutral-500 mb-1">
              Owner
            </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setDropdownQuery("");
                      setOpenDropdown(
                        openDropdown === "owner" ? null : "owner"
                      );
                    }}
                    className={`w-full justify-between ${filterPillClass(!!meta.owner_id)}`}
                  >
                    <span className="truncate">
                      {(
                        allFilterOptions.owners.find(
                          (o) => o.id === meta.owner_id
                        ) || {}
                      ).name || "Unassigned"}
                    </span>
                    <ChevronDown />
                  </button>
                  {openDropdown === "owner" && (
                    <>
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => setOpenDropdown(null)}
                      />
                      <div className="absolute z-30 mt-1 left-0 w-full max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
                        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 px-2 pt-1 pb-1 border-b border-gray-200 dark:border-gray-700">
                          <input
                            type="text"
                            autoFocus
                            value={dropdownQuery}
                            onChange={(e) => setDropdownQuery(e.target.value)}
                            placeholder="Search…"
                            className="w-full px-1 py-0.5 text-[11px] bg-transparent text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setMeta({ ...meta, owner_id: null });
                            setOpenDropdown(null);
                          }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${!meta.owner_id ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-400"}`}
                        >
                          <span>Unassigned</span>
                          {!meta.owner_id && <CheckIcon />}
                        </button>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        {allFilterOptions.owners
                          .filter((o) =>
                            !dropdownQuery ||
                            (o.name || "")
                              .toLowerCase()
                              .includes(dropdownQuery.toLowerCase())
                          )
                          .map((o) => {
                            const sel = o.id === meta.owner_id;
                            return (
                              <button
                                key={o.id}
                                type="button"
                                onClick={() => {
                                  setMeta({ ...meta, owner_id: o.id });
                                  setOpenDropdown(null);
                                }}
                                className={`flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${sel ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-700 dark:text-gray-300"}`}
                              >
                                <span className="truncate">{o.name}</span>
                                {sel && <CheckIcon />}
                              </button>
                            );
                          })}
                        {allFilterOptions.owners.length > 0 &&
                          allFilterOptions.owners.filter((o) =>
                            !dropdownQuery ||
                            (o.name || "")
                              .toLowerCase()
                              .includes(dropdownQuery.toLowerCase())
                          ).length === 0 && (
                            <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">
                              No matches
                            </div>
                          )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Client */}
              <div>
                <label className={`block text-xs ${popupSubtext} mb-1`}>
                  Client
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setDropdownQuery("");
                      setOpenDropdown(
                        openDropdown === "client" ? null : "client"
                      );
                    }}
                    className={`w-full justify-between ${filterPillClass(!!meta.client_id)}`}
                  >
                    <span className="truncate">
                      {(
                        allFilterOptions.clients.find(
                          (c) => c.id === meta.client_id
                        ) || {}
                      ).name || "Internal (no client)"}
                    </span>
                    <ChevronDown />
                  </button>
                  {openDropdown === "client" && (
                    <>
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => setOpenDropdown(null)}
                      />
                      <div className="absolute z-30 mt-1 left-0 w-full max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
                        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 px-2 pt-1 pb-1 border-b border-gray-200 dark:border-gray-700">
                          <input
                            type="text"
                            autoFocus
                            value={dropdownQuery}
                            onChange={(e) => setDropdownQuery(e.target.value)}
                            placeholder="Search…"
                            className="w-full px-1 py-0.5 text-[11px] bg-transparent text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setMeta({ ...meta, client_id: null });
                            setOpenDropdown(null);
                          }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${!meta.client_id ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-400"}`}
                        >
                          <span>Internal (no client)</span>
                          {!meta.client_id && <CheckIcon />}
                        </button>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        {allFilterOptions.clients
                          .filter((c) =>
                            !dropdownQuery ||
                            (c.name || "")
                              .toLowerCase()
                              .includes(dropdownQuery.toLowerCase())
                          )
                          .map((c) => {
                            const sel = c.id === meta.client_id;
                            return (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => {
                                  setMeta({ ...meta, client_id: c.id });
                                  setOpenDropdown(null);
                                }}
                                className={`flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${sel ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-700 dark:text-gray-300"}`}
                              >
                                <span className="truncate">{c.name}</span>
                                {sel && <CheckIcon />}
                              </button>
                            );
                          })}
                        {allFilterOptions.clients.length > 0 &&
                          allFilterOptions.clients.filter((c) =>
                            !dropdownQuery ||
                            (c.name || "")
                              .toLowerCase()
                              .includes(dropdownQuery.toLowerCase())
                          ).length === 0 && (
                            <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">
                              No matches
                            </div>
                          )}
                      </div>
                    </>
                  )}
                </div>
              </div>
        </div>

        {/* Dropdown row 2 — Service + Work Type */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Service */}
          <div>
            <label className="block text-xs text-neutral-500 mb-1">
              Service
            </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === "service" ? null : "service"
                      )
                    }
                    className={`w-full justify-between ${filterPillClass(!!meta.service)}`}
                  >
                    <span className="truncate">
                      {(
                        allFilterOptions.services.find(
                          (s) => s.code === meta.service
                        ) || {}
                      ).label || "None"}
                    </span>
                    <ChevronDown />
                  </button>
                  {openDropdown === "service" && (
                    <>
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => setOpenDropdown(null)}
                      />
                      <div className="absolute z-30 mt-1 left-0 w-full max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
                        <button
                          type="button"
                          onClick={() => {
                            setMeta({ ...meta, service: "" });
                            setOpenDropdown(null);
                          }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${!meta.service ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-400"}`}
                        >
                          <span>None</span>
                          {!meta.service && <CheckIcon />}
                        </button>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        {allFilterOptions.services.map((s) => {
                          const sel = s.code === meta.service;
                          return (
                            <button
                              key={s.code}
                              type="button"
                              onClick={() => {
                                setMeta({ ...meta, service: s.code });
                                setOpenDropdown(null);
                              }}
                              className={`flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${sel ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-700 dark:text-gray-300"}`}
                            >
                              <span className="truncate">{s.label}</span>
                              {sel && <CheckIcon />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
          {/* Work Type */}
          <div>
            <label className="block text-xs text-neutral-500 mb-1">
              Work Type
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() =>
                  setOpenDropdown(
                    openDropdown === "workType" ? null : "workType"
                  )
                }
                className={`w-full justify-between ${filterPillClass(!!meta.work_type)}`}
              >
                <span className="truncate">
                  {(
                    workTypeOptions.find(
                      (o) => o.value === meta.work_type
                    ) || {}
                  ).label || "Select…"}
                </span>
                <ChevronDown />
              </button>
              {openDropdown === "workType" && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setOpenDropdown(null)}
                  />
                  <div className="absolute z-30 mt-1 left-0 w-full max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
                    {workTypeOptions.map((o) => {
                      const sel = o.value === meta.work_type;
                      return (
                        <button
                          key={o.value}
                          type="button"
                          onClick={() => {
                            setMeta({ ...meta, work_type: o.value });
                            setOpenDropdown(null);
                          }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${sel ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-700 dark:text-gray-300"}`}
                        >
                          <span className="truncate">{o.label}</span>
                          {sel && <CheckIcon />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Dropdown row 3 — Priority + Due Date */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Priority */}
          <div>
            <label className="block text-xs text-neutral-500 mb-1">
              Priority
            </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === "priority" ? null : "priority"
                      )
                    }
                    className={`w-full justify-between ${filterPillClass(!!meta.priority)}`}
                  >
                    <span className="truncate">
                      {PRIORITY_LABELS[meta.priority] || "None"}
                    </span>
                    <ChevronDown />
                  </button>
                  {openDropdown === "priority" && (
                    <>
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => setOpenDropdown(null)}
                      />
                      <div className="absolute z-30 mt-1 left-0 w-full max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
                        <button
                          type="button"
                          onClick={() => {
                            setMeta({ ...meta, priority: null });
                            setOpenDropdown(null);
                          }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${!meta.priority ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-400"}`}
                        >
                          <span>None</span>
                          {!meta.priority && <CheckIcon />}
                        </button>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        {PRIORITY_OPTIONS.map((p) => {
                          const sel = p.value === meta.priority;
                          return (
                            <button
                              key={p.value}
                              type="button"
                              onClick={() => {
                                setMeta({ ...meta, priority: p.value });
                                setOpenDropdown(null);
                              }}
                              className={`flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${sel ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-700 dark:text-gray-300"}`}
                            >
                              <span>{p.label}</span>
                              {sel && <CheckIcon />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Due date */}
          <div>
            <label className="block text-xs text-neutral-500 mb-1">
              Due date
            </label>
            <input
              type="date"
              value={meta.due || ""}
              onChange={(e) =>
                setMeta({ ...meta, due: e.target.value || null })
              }
              className={`w-full rounded-md ${popupInput} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none`}
            />
          </div>
        </div>

        {/* Description */}
        <div className="mb-3">
          <label className="block text-xs text-neutral-500 mb-1">
            Description (optional)
          </label>
          <textarea
            ref={descRef}
            defaultValue={initialValues?.description || ""}
            placeholder="Description (optional)"
            className={`w-full rounded-lg ${popupInput} p-3 text-sm resize-none min-h-[80px] max-h-[200px] focus:border-blue-500 outline-none mb-3`}
          />
        </div>

        {/* Files (work-page-fixes §C) — drop zone, file picker, paste, and the
            pending file list with per-row status. Nothing uploads until Create
            is pressed; uploads run after fn_work_create returns the task id. */}
        <div className="mb-3 md:mb-4">
          <label className="block text-xs text-neutral-500 mb-1">
            Files (optional)
          </label>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
            }}
            className={`flex items-center justify-center gap-2 rounded-lg border border-dashed p-4 text-sm cursor-pointer transition-colors ${
              dragOver
                ? "border-blue-500 bg-blue-500/10 text-blue-300"
                : `${popupInput} text-neutral-400 hover:border-neutral-400`
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <PaperclipIcon className="w-4 h-4 flex-shrink-0" />
            <span>Drop files here, paste a screenshot, or </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              className="text-blue-500 hover:text-blue-400 underline"
            >
              Choose files
            </button>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          />
          {files.length > 0 && (
            <div className="mt-2 space-y-1">
              {files.map((f) => (
                <div
                  key={f.key}
                  className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-800/50"
                >
                  {f.thumbUrl ? (
                    <img
                      src={f.thumbUrl}
                      alt=""
                      className="w-8 h-8 rounded object-cover flex-shrink-0"
                    />
                  ) : (
                    <FileIcon className="w-4 h-4 flex-shrink-0 text-neutral-500" />
                  )}
                  <span
                    className="flex-1 text-sm text-neutral-300 overflow-hidden text-ellipsis whitespace-nowrap"
                    title={f.file.name}
                  >
                    {f.file.name}
                  </span>
                  {f.status === "uploading" && (
                    <SpinnerIcon className="w-4 h-4 flex-shrink-0 text-neutral-400" />
                  )}
                  {f.status === "done" && (
                    <CheckIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-500" />
                  )}
                  {f.status === "failed" && (
                    <span className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-red-400" title={f.error}>
                        Failed
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          createdTaskId && uploadOne(createdTaskId, f)
                        }
                        className="text-xs text-blue-500 hover:text-blue-400"
                      >
                        Retry
                      </button>
                    </span>
                  )}
                  {f.status === "pending" && (
                    <button
                      type="button"
                      onClick={() => removeFile(f.key)}
                      className="flex-shrink-0 text-neutral-500 hover:text-red-400 text-xs"
                      aria-label="Remove file"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tags */}
        <div className="mb-3 md:mb-4">
          <label className="block text-xs text-neutral-500 mb-1">
            Tags
          </label>
                <input
                  type="text"
                  value={meta.tags.join(", ")}
                  onChange={(e) =>
                    setMeta({
                      ...meta,
                      tags: e.target.value
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="tag1, tag2, tag3"
                  className={`w-full rounded-md ${popupInput} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none`}
                />
                {existingTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {existingTags.map((t) => {
                      const selected = meta.tags.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() =>
                            setMeta((prev) =>
                              selected
                                ? {
                                    ...prev,
                                    tags: prev.tags.filter((x) => x !== t),
                                  }
                                : { ...prev, tags: [...prev.tags, t] }
                            )
                          }
                          className={`px-2 py-0.5 text-xs rounded-full border ${
                            selected
                              ? "bg-blue-600 text-white border-blue-600"
                              : `${popupInput} ${popupSubtext} hover:border-blue-500`
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

        {/* Repeat this task (work-page-fixes §B) — collapsed by default.
            Expanding reveals the cadence dropdown and the matching day picker. */}
        <div className="mb-3 md:mb-4 border-t border-gray-200 dark:border-gray-700 pt-3">
          <div className="flex items-center justify-between cursor-pointer select-none">
            <span className={`text-xs font-medium ${popupSubtext}`}>
              Repeat this task
            </span>
            <button
              type="button"
              onClick={() => setRepeatEnabled(!repeatEnabled)}
              role="switch"
              aria-checked={repeatEnabled}
              aria-label="Repeat this task"
              className={`relative inline-flex items-center w-8 h-4 rounded-full transition-colors ${repeatEnabled ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"}`}
            >
              <span className={`inline-block w-3 h-3 rounded-full bg-white transition-transform ${repeatEnabled ? "translate-x-4" : "translate-x-1"}`} />
            </button>
          </div>
          {repeatEnabled && (
            <div className="mt-3 pl-6">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className={`block text-xs ${popupSubtext} mb-1`}>
                    How often
                  </label>
                  <select
                    value={repeatCadence}
                    onChange={(e) => setRepeatCadence(e.target.value)}
                    className={`w-full rounded-md ${popupInput} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none`}
                  >
                    {CADENCE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                {repeatCadence === "weekly" && (
                  <div className="flex-1">
                    <label className={`block text-xs ${popupSubtext} mb-1`}>
                      On
                    </label>
                    <select
                      value={repeatDayOfWeek ?? 1}
                      onChange={(e) => setRepeatDayOfWeek(Number(e.target.value))}
                      className={`w-full rounded-md ${popupInput} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none`}
                    >
                      {WEEKDAY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {DAY_OF_MONTH_CADENCES.has(repeatCadence) && (
                  <div className="flex-1">
                    <label className={`block text-xs ${popupSubtext} mb-1`}>
                      On
                    </label>
                    <select
                      value={repeatDayOfMonth ?? 1}
                      onChange={(e) => setRepeatDayOfMonth(Number(e.target.value))}
                      className={`w-full rounded-md ${popupInput} px-3 py-2 text-sm focus:border-blue-500 focus:outline-none`}
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <p className={`text-[11px] ${popupSubtext} mt-3`}>
                Its subtasks are copied into every new copy.
              </p>
            </div>
          )}
        </div>

        </div>

        {/* Pinned footer — Cancel/Create stay fixed at the bottom while the
            body above scrolls. It is a flex sibling of the scrollable body, so
            it never scrolls out of view. (work-page-fixes Item 1) */}
        <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            className={`px-3 py-1.5 text-sm ${popupBtn}`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !title.trim() || !!createdTaskId}
            className="px-4 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Creating...
              </>
            ) : (
              "Create"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTaskModal;
